import {
  AUDIT_COLUMN_DEFINITIONS,
  AUDIT_GROUP_AVAILABLE_COLUMNS,
  DEFAULT_VISIBLE_COLUMNS,
  FIXED_AUDIT_COLUMNS,
  PRIORITY_AUDIT_COLUMNS,
} from './constants';
import type {
  AppliedColumnFilters,
  AuditLogColumnFilters,
  AuditLogColumnOption,
} from './types';

/**
 * Get active/checked column filters from column filters array
 */
export const getActiveFiltersForColumns = (
  columnFilters: AuditLogColumnFilters
): AppliedColumnFilters => {
  const checkedOptions: AppliedColumnFilters = {};

  columnFilters.forEach((filter) => {
    checkedOptions[filter.value] = [];
    filter.options.forEach((option) => {
      if (option.isChecked) {
        checkedOptions[filter.value].push(option.value);
      }
    });
  });

  return checkedOptions;
};

/**
 * Create column options for a specific group
 */
const createColumnOptions = (groupKey: string): AuditLogColumnOption[] => {
  const groupPrefix = groupKey.toUpperCase();
  const options: AuditLogColumnOption[] = [];

  // Get columns that belong to this group, optionally constrained by allowlist
  const allowlist = AUDIT_GROUP_AVAILABLE_COLUMNS[groupPrefix];

  Object.entries(AUDIT_COLUMN_DEFINITIONS).forEach(
    ([columnKey, columnLabel]) => {
      const belongsToGroup = columnKey.startsWith(`${groupPrefix}-`);
      const isAllowed = Array.isArray(allowlist)
        ? allowlist.includes(columnKey)
        : true;

      if (belongsToGroup && isAllowed) {
        const isDefaultVisible =
          !!DEFAULT_VISIBLE_COLUMNS[
            groupPrefix as keyof typeof DEFAULT_VISIBLE_COLUMNS
          ].includes(columnKey);

        options.push({
          label: columnLabel,
          value: columnKey,
          isChecked: isDefaultVisible,
          isFixed: FIXED_AUDIT_COLUMNS.includes(columnKey),
        });
      }
    }
  );

  // Sort options by priority
  return options.sort(
    (a, b) =>
      PRIORITY_AUDIT_COLUMNS.indexOf(b.value) -
      PRIORITY_AUDIT_COLUMNS.indexOf(a.value)
  );
};

/**
 * Initialize audit log column filters with default configuration
 */
export const initializeAuditLogColumnFilters = (): AuditLogColumnFilters => {
  const columnFilters: AuditLogColumnFilters = [];

  // Create filters for each group
  const groups = [
    {
      key: 'sec_audit_log',
      label: 'Security Audit Log',
      value: 'SEC_AUDIT_LOG',
    },
    { key: 'change_log', label: 'Change Log', value: 'CHANGE_LOG' },
    { key: 'tcode_log', label: 'T-Code Log', value: 'TCODE_LOG' },
    { key: 'system_log', label: 'System Log', value: 'SYSTEM_LOG' },
  ];

  groups.forEach((group) => {
    const options = createColumnOptions(group.key);

    if (options.length > 0) {
      columnFilters.push({
        label: group.label,
        value: group.value,
        isDisabled: false,
        options,
      });
    }
  });

  // Sort by predefined order
  const sortOrder = ['SEC_AUDIT_LOG', 'CHANGE_LOG', 'TCODE_LOG', 'SYSTEM_LOG'];

  return columnFilters.sort(
    (a, b) => sortOrder.indexOf(a.value) - sortOrder.indexOf(b.value)
  );
};

/**
 * Get visible column keys from applied filters
 */
export const getVisibleColumnKeys = (
  appliedFilters: AppliedColumnFilters
): string[] => {
  const visibleColumns: string[] = [];

  Object.values(appliedFilters).forEach((columnArray) => {
    visibleColumns.push(...columnArray);
  });

  return visibleColumns;
};

/**
 * From applied filters, compute allowed base keys per log group
 */
export const getVisibleBaseKeysByGroup = (
  appliedFilters: AppliedColumnFilters
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};

  Object.entries(appliedFilters).forEach(([groupKey, keys]) => {
    const baseKeys = keys.map((full) => full.replace(`${groupKey}-`, ''));

    result[groupKey] = baseKeys;
  });

  return result;
};

/**
 * Check if a column should be displayed based on applied filters
 */
export const isColumnVisible = (
  columnKey: string,
  appliedFilters: AppliedColumnFilters
): boolean => {
  const visibleColumns = getVisibleColumnKeys(appliedFilters);

  return visibleColumns.includes(columnKey);
};

/**
 * Get column label from column key
 */
export const getColumnLabel = (columnKey: string): string => {
  const defs = AUDIT_COLUMN_DEFINITIONS as Partial<Record<string, string>>;

  return defs[columnKey] ?? columnKey;
};
